import * as Dfns from 'date-fns/fp';
import { useCallback, useEffect, useState } from 'react';
import { Option, parseRawTokenArray } from '@blisspointmedia/bpm-types/dist/FilterBar';
import { Category } from '@blisspointmedia/bpm-types/dist/FilterPane';
import {
  GroupedSegment,
  isBillingSegmentName,
  SegmentationMappingLabelRule
} from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { useSetError } from '@src/redux/modals';
import { awaitJSON, SegmentationMappingLambdaFetch } from '../../utils/fetch-utils';
import {
  BillingMappingLabelRule,
  CustomSegmentsData,
  SystemGeneratedSegmentNames
} from './segmentation-mapping-constants';

// Rule-application utilities now live in bpm-types; re-exported here so existing importers of
// this module keep resolving them from one place.
export {
  isBillingFullyCoveredForDateRange,
  isNewBillingSegmentValue
} from '@blisspointmedia/bpm-types/dist/SegmentationMapping';

interface IfStatementToken {
  label: string;
}

export type { CustomSegmentsData } from './segmentation-mapping-constants';

export const getSegmentName = (segmentId: number, customSegments: CustomSegmentsData[]): string =>
  customSegments?.find(segment => Number(segment.segmentId) === segmentId)?.segmentName || '';

export const getSegmentValue = (valueId: number, customSegments: CustomSegmentsData[]): string =>
  customSegments
    ?.flatMap(segment => segment.values)
    .find(value => Number(value.valueId) === valueId)?.valueName || '';

export const buildIfStatement = (
  filterTokens: IfStatementToken[] | string,
  isAdvanced: boolean
): string =>
  isAdvanced && Array.isArray(filterTokens)
    ? filterTokens.map(token => token.label).join(' ')
    : `* is like ${
        Array.isArray(filterTokens)
          ? filterTokens.map(filter => filter.label).join(' or ')
          : filterTokens
      }`;

export const formatDate = (dateStr: string): string =>
  Dfns.format('MM/dd/yyyy', Dfns.parseISO(dateStr));

export const formatDateRange = (
  start: string | null | undefined,
  end: string | null | undefined
): string =>
  start ? `${formatDate(start)} — ${end ? formatDate(end) : 'No end date'}` : 'Not Set';

export const isBillingRule = (
  rule: SegmentationMappingLabelRule | BillingMappingLabelRule
): rule is BillingMappingLabelRule => 'startDate' in rule;

export const getFilterBarOptions = (
  customSegments: CustomSegmentsData[] | undefined,
  dataGranularity: 'ad' | 'ad_group' | 'campaign'
): Option[] => {
  const uniqueSegmentOptions =
    customSegments?.map(segment => ({
      name: segment.segmentName,
      label: segment.segmentName
    })) ?? [];

  const options = [
    ...uniqueSegmentOptions,
    { name: 'account_id', label: 'Account ID' },
    { name: 'account_name', label: 'Account Name' },
    { name: 'campaign_id', label: 'Campaign ID' },
    { name: 'campaign_name', label: 'Campaign' }
  ];

  if (dataGranularity === 'ad_group' || dataGranularity === 'ad') {
    options.push({ name: 'ad_group_id', label: 'Ad Group ID' });
    options.push({ name: 'ad_group_name', label: 'Ad Group' });
  }

  if (dataGranularity === 'ad') {
    options.push({ name: 'ad_id', label: 'Ad ID' });
    options.push({ name: 'ad_name', label: 'Ad' });
  }
  return options;
};

// The entity name field(s) Basic mode is allowed to match, derived from getFilterBarOptions's
// output below rather than duplicating the granularity branching, so the two lists can't drift.
const BASIC_FILTERABLE_FIELDS = new Set(['campaign_name', 'ad_group_name', 'ad_name']);

// Basic mode's free-text term matches only the entity name(s) at this data granularity — never
// Account, ID fields, or custom segments (Advanced mode keeps the full filterBarOptions).
export const getBasicFilterBarOptions = (filterBarOptions: Option[]): Option[] =>
  filterBarOptions.filter(option => BASIC_FILTERABLE_FIELDS.has(option.name ?? ''));

export const getCategories = (
  filterBarOptions: Option[],
  data: Record<string, unknown>[],
  customSegments: CustomSegmentsData[]
): Category[] => {
  const sharedOptions = [
    'account_id',
    'account_name',
    'campaign_id',
    'campaign_name',
    'ad_group_id',
    'ad_group_name',
    'ad_id',
    'ad_name',
    'Platform',
    'Channel'
  ];
  return (
    filterBarOptions?.map(option => {
      if (option.name && sharedOptions.includes(option.name)) {
        const seen = new Set<string>();
        const options: { label: string; key: string; value: string }[] = [];
        data?.forEach(item => {
          const value = String(option.name ? item[option.name] : undefined);
          if (seen.has(value)) return;
          seen.add(value);
          options.push({ label: value, key: value, value });
        });
        return {
          label: String(option.label),
          key: String(option.name),
          options
        };
      }
      const options = customSegments?.find(segment => segment.segmentName === option.name)?.values;
      return {
        label: option.label,
        key: String(option.name),
        options:
          options?.map(value => ({
            label: value.valueName,
            key: String(value.valueId),
            value: String(value.valueId)
          })) || []
      };
    }) || []
  );
};

const operators = {
  '=': 'is',
  '!=': 'is not',
  '~': 'is like',
  '!~': 'is not like'
};

export const filterStringToOptions = (
  filterString: string,
  categories: Option[],
  data: Record<string, unknown>[]
): Option[] => {
  const filterArray = filterString
    .split(
      new RegExp(
        `(\\(|\\)|\\s+and\\s+|\\s+or\\s+|${Object.keys(operators)
          .map(op => `\\s*${op}\\s*`)
          .join('|')})`
      )
    )
    .map(token => token.trim())
    .filter(token => token !== '');

  // Replace the operators with their values
  const operatorsRegex = new RegExp(Object.keys(operators).join('|'), 'g');
  const filterArrayWithOperators = filterArray
    .map(token => {
      if (operatorsRegex.test(token)) {
        return token.replace(operatorsRegex, match => operators[match]);
      }
      return token;
    })
    .map(token => token.replace(/"/g, ''))
    .filter(token => token !== '');

  const parsedTokens = parseRawTokenArray(filterArrayWithOperators, categories, data);

  return parsedTokens;
};

export const filterTokensToString = (
  filterTokens: Option[],
  categories: Option[],
  isAdvanced: boolean
): string => {
  if (isAdvanced) {
    const filterString = filterTokens
      .map(token => {
        if (token.type === 'cat') {
          return `"${token.label}"`;
        }
        if (token.type === 'op') {
          return Object.keys(operators).find(key => operators[key] === token.key);
        }
        if (token.type === 'val') {
          const category = categories.find(cat => cat.key === token.key);
          const value = category?.key === token.key ? { label: token.label } : undefined;
          return value ? value.label : token.key;
        }
        if (token.type === 'bool') {
          return token.label;
        }
        if (token.type === 'openParen' || token.type === 'closeParen') {
          return token.label;
        }
        return '';
      })
      .join(' ');

    return filterString;
  }
  const filterString = filterTokens.map(token => token.label).join(', ');
  return filterString;
};

export const useGroupedSegments = ({
  company,
  shouldEnableMetricsTableSegmentGroups
}: {
  company: string;
  shouldEnableMetricsTableSegmentGroups: boolean;
}): {
  groupedSegments: Record<string, GroupedSegment[]>;
  fetchGroupedSegments: () => void;
} => {
  const [groupedSegments, setGroupedSegments] = useState<Record<string, GroupedSegment[]>>({});
  const setError = useSetError();

  const fetchGroupedSegments = useCallback(() => {
    SegmentationMappingLambdaFetch('/getGroupedSegments', { params: { company } })
      .then(awaitJSON)
      .then(setGroupedSegments)
      .catch(e =>
        setError({ title: "Couldn't load grouped segments", message: e.message, reportError: e })
      );
  }, [company, setError]);

  // Keyed on company directly (rather than on fetchGroupedSegments) so the fetch doesn't
  // re-fire when the callback gets a new identity during the same company/flag state.
  useEffect(() => {
    if (!shouldEnableMetricsTableSegmentGroups || !company) {
      return undefined;
    }
    let cancelled = false;
    SegmentationMappingLambdaFetch('/getGroupedSegments', { params: { company } })
      .then(awaitJSON)
      .then(data => {
        if (!cancelled) {
          setGroupedSegments(data);
        }
      })
      .catch(e => {
        if (cancelled) return;
        setError({ title: "Couldn't load grouped segments", message: e.message, reportError: e });
      });
    return () => {
      cancelled = true;
    };
  }, [company, setError, shouldEnableMetricsTableSegmentGroups]);

  return { groupedSegments, fetchGroupedSegments };
};

export const sortCustomSegments = (customSegments: CustomSegmentsData[]): CustomSegmentsData[] => {
  customSegments.sort((a, b) => {
    const segmentNameToRelativeValue = (segName: string) => {
      switch (true) {
        case segName === SystemGeneratedSegmentNames.CHANNEL:
          return 1;
        case segName === SystemGeneratedSegmentNames.PLATFORM:
          return 2;
        case isBillingSegmentName(segName):
          return 3;
        default:
          return 99;
      }
    };

    if (segmentNameToRelativeValue(a.segmentName) < segmentNameToRelativeValue(b.segmentName)) {
      return -1;
    }
    if (segmentNameToRelativeValue(a.segmentName) > segmentNameToRelativeValue(b.segmentName)) {
      return 1;
    }

    return a.segmentName.localeCompare(b.segmentName);
  });

  customSegments.forEach(seg => {
    seg.values.sort((a, b) => {
      if (a.valueName === 'N/A') {
        return -1;
      }
      if (b.valueName === 'N/A') {
        return 1;
      }
      return a.valueName.localeCompare(b.valueName);
    });
  });
  return customSegments;
};
